/**
 * Route-level guard keyed on a tab's resolved access from ``/api/me`` ``tabs``.
 *
 * Generalizes the old per-feature guard: access is now admin-configurable per
 * tab (mode + allowlist), so the frontend just reads ``tabs[key].accessible``
 * rather than hard-coding the admin bit or a feature string. The backend enforces
 * independently; this only keeps unauthorized users off a broken page.
 *
 * ``listed`` (nav visibility) is separate — a tab can be accessible by direct
 * link yet hidden from the bar — so this gate checks ``accessible`` only.
 *
 * On denial it renders an inline access-denied message rather than redirecting.
 * Every tab (including Search) can be gated, so a redirect could bounce to a tab
 * the user also can't reach; an inline message is loop-free.
 */
import type { ReactElement } from "react";
import { useMe } from "@/auth/useMe";

export function TabGate({ tab, children }: { tab: string; children: ReactElement }) {
  const { data, isLoading } = useMe();

  if (isLoading) {
    return (
      <p className="font-sans text-sm text-bw-ink-muted">Checking permissions…</p>
    );
  }
  if (!data?.tabs?.[tab]?.accessible) {
    return (
      <div className="space-y-1">
        <h1 className="font-sans text-lg font-semibold text-bw-navy">No access</h1>
        <p className="font-sans text-sm text-bw-ink-muted">
          You don't have access to this view. Ask an admin if you think you should.
        </p>
      </div>
    );
  }
  return children;
}
